import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Table from '../../components/ui/Table';
import api from '../../services/api';

function ExpiryBadge({ days }) {
  if (days === undefined || days === null) return <span style={{ color: '#CBD5E1' }}>&mdash;</span>;
  if (days <= 0) return <Badge variant="error">Просрочен</Badge>;
  if (days <= 3) return <Badge variant="urgent">{days} дн.</Badge>;
  if (days <= 7) return <Badge variant="warning">{days} дн.</Badge>;
  return <Badge variant="success">{days} дн.</Badge>;
}

export default function ProductCatalog() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterDept] = useState('');
  const [filterBrand] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: '1', per_page: '100' });
        if (search) params.set('search', search);
        if (filterDept) params.set('department', filterDept);
        if (filterBrand) params.set('brand', filterBrand);
        const res = await api.get(`/v1/products?${params}`);
        if (res?.data) setProducts(res.data);
        if (res?.meta?.total) setTotal(res.meta.total);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [search, filterDept, filterBrand]);

  const columns = [
    {
      key: 'name', label: 'Товар',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#E6FAFB',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Package size={16} color="#009995" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</div>
            <div style={{ fontSize: 11, color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[row.department, row.category_name, row.subcategory].filter(Boolean).join(' / ')}
            </div>
          </div>
        </div>
      )
    },
    { key: 'source_code', label: 'Код', render: v => <code style={{ fontSize: 11, background: '#F1F5F9', padding: '3px 8px', borderRadius: 5, color: '#475569' }}>{v || '\u2014'}</code> },
    { key: 'barcode', label: 'Штрих-код', render: v => <code style={{ fontSize: 11, fontFamily: 'monospace', color: '#009995', background: '#E6FAFB', padding: '2px 6px', borderRadius: 4 }}>{v || '\u2014'}</code> },
    { key: 'brand', label: 'Бренд', render: v => v ? <Badge variant="primary">{v}</Badge> : <span style={{ color: '#CBD5E1' }}>&mdash;</span> },
    { key: 'lsp', label: 'LSP', render: v => v ? <span style={{ fontWeight: 600, color: '#009995' }}>{Number(v).toLocaleString()} сум</span> : <span style={{ color: '#CBD5E1' }}>&mdash;</span> },
    { key: 'days_until_expiry', label: 'ОСГ', render: v => <ExpiryBadge days={v} /> },
    { key: 'is_active', label: 'Статус', render: v => <Badge variant={v ? 'success' : 'error'} dot>{v ? 'Активен' : 'Неактивен'}</Badge> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>Каталог товаров</h1>
        <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 2, fontWeight: 500 }}>Всего: {total || products.length} позиций</p>
      </div>

      <Card padding="0">
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #F1F5F9', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#F8FAFC', borderRadius: 10, padding: '8px 14px',
            flex: 1, minWidth: 200, border: '1.5px solid #E2E8F0',
          }}>
            <Search size={14} color="#94A3B8" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по названию, коду, штрих-коду, бренду..."
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', fontFamily: 'inherit', color: '#0F172A' }} />
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid rgba(0,206,238,0.15)', borderTopColor: '#00CEEE', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
            Загрузка...
          </div>
        ) : (
          <Table columns={columns} data={products}
            onRowClick={(row) => navigate(`/products/${row.id}`)}
            emptyText="Товары не найдены. Данные поступают из 1С через API синхронизации." />
        )}
      </Card>
    </div>
  );
}
